import { ProcessStep } from './processStepModel';

interface ProcessStepperProps {
  steps: ProcessStep[];
  selectedKey: string;
  onSelect: (key: string) => void;
}

function CheckMark() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 20 20'
      fill='none'
      aria-hidden='true'
    >
      <path
        d='M4.6 10.6l3.1 3.1 7.7-7.7'
        stroke='currentColor'
        strokeWidth='2.4'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function CrossMark() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 20 20'
      fill='none'
      aria-hidden='true'
    >
      <path
        d='M6 6l8 8M14 6l-8 8'
        stroke='currentColor'
        strokeWidth='2.4'
        strokeLinecap='round'
      />
    </svg>
  );
}

const stepGlyph = (step: ProcessStep, index: number) => {
  if (!step.isComplete) return index + 1;
  if (step.doneTone === 'danger') return <CrossMark />;
  return <CheckMark />;
};

const doneDotClasses = (tone?: string) => {
  if (tone === 'danger') return 'bg-danger border-danger text-white';
  if (tone === 'warning') return 'bg-warning border-warning text-gray-900';
  return 'bg-success border-success text-white';
};

const dotClassesFor = (step: ProcessStep, isCurrent: boolean): string => {
  if (step.isComplete) return doneDotClasses(step.doneTone);
  if (isCurrent) return 'border-primary text-primary bg-blue-50';
  return 'border-gray-300 text-gray-400 bg-white';
};

const labelClassesFor = (isSelected: boolean, selectable: boolean): string => {
  if (isSelected) return 'text-primary';
  if (selectable) return 'text-gray-900';
  return 'text-gray-400';
};

export function ProcessStepper({
  steps,
  selectedKey,
  onSelect,
}: ProcessStepperProps) {
  const currentIndex = steps.findIndex((step) => !step.isComplete);

  return (
    <div
      className='flex items-start overflow-x-auto px-1'
      aria-label='Process stages'
    >
      {steps.map((step, index) => {
        const isCurrent = index === currentIndex;
        const selectable = step.isComplete;
        const isSelected = step.key === selectedKey;

        const dot = dotClassesFor(step, isCurrent);

        return (
          <button
            key={step.key}
            type='button'
            aria-current={isSelected ? 'step' : undefined}
            disabled={!selectable}
            onClick={() => onSelect(step.key)}
            className={`relative flex-1 min-w-[80px] flex flex-col items-center gap-2 px-1 pb-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ${
              selectable ? 'cursor-pointer' : 'cursor-default'
            }`}
          >
            {index > 0 && (
              <span
                className={`absolute top-[18px] -left-1/2 w-full h-0.5 ${
                  step.isComplete || isCurrent ? 'bg-success' : 'bg-gray-200'
                }`}
              />
            )}
            <span
              className={`relative z-10 w-9 h-9 rounded-full border-2 grid place-items-center text-sm font-bold ${dot}`}
            >
              {stepGlyph(step, index)}
            </span>
            <span
              className={`text-xs font-semibold text-center leading-tight ${labelClassesFor(
                isSelected,
                selectable
              )}`}
            >
              {step.label}
            </span>
            {isSelected && (
              <span className='absolute bottom-0 w-6 h-0.5 rounded bg-primary' />
            )}
          </button>
        );
      })}
    </div>
  );
}
